import isNil from "lodash/isNil";

import { TextTestID } from "@/constants/TestID";
import type { GroupChannel } from "@/crossPlatform/Sendbird";
import useWebSafeChatPatientId from "@/hooks/useWebSafeChatPatientId";
import useAppMonitor from "@/services/AppMonitor";
import { useGetPatientDocumentsQuery } from "@/store/api/chat";
import AttachmentDocumentCard from "@/ui/components/messages/documents/AttachmentDocumentCard";
import { Text } from "@/ui/core";
import { FlatList } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

interface ChannelDocumentListProps extends ViewWithTestID {
  channel: GroupChannel;
}

const ChannelDocumentList = ({ channel, testID }: ChannelDocumentListProps) => {
  const baseTestID = joinTestIDs(testID, "document-list");
  const { captureMessage } = useAppMonitor();
  const selectedPatientId = useWebSafeChatPatientId(channel);
  const isMissingPatientId = isNil(selectedPatientId);
  const { data, isLoading } = useGetPatientDocumentsQuery(
    {
      channelId: channel.url,
      patientExternalId: selectedPatientId || "",
    },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- skip naming is defined by RTKQuery
      skip: isMissingPatientId,
    },
  );

  if (isMissingPatientId) {
    captureMessage("selectedPatientId is missing from chat-channel-context");
    return null;
  }

  const emptyListComponent = () => (
    <Text testID={joinTestIDs(baseTestID, TextTestID.EmptyList)}>
      No files have been added to this channel
    </Text>
  );

  return (
    <FlatList
      data={data}
      emptyListComponent={emptyListComponent}
      isDataLoading={isLoading}
      keyExtractor={(item) => item.documentExternalId}
      renderItem={({ item }) => {
        return (
          <AttachmentDocumentCard
            channel={channel}
            document={item}
            testID={testID}
          />
        );
      }}
      testID={baseTestID}
    />
  );
};

export type { ChannelDocumentListProps };
export default ChannelDocumentList;
